/** Small pieces shared by the sales screens: status chips, money, messages with dates, quick actions. */
import type { ReactNode } from "react";
import { localDate, type Money, type Msg, type SubjectRef, type Tone } from "../../../contract";
import { api } from "../../../web/lib/api";
import { formatDate, formatMoney } from "../../../web/lib/format";
import { plural, t } from "../../../web/lib/i18n";
import { useMe } from "../../../web/lib/session";
import { invalidateAll } from "../../../web/lib/query";
import { openRecord } from "../../../web/forms/EventForm";
import { Chip, Icon, cx, toast } from "../../../web/ui/kit";
import {
  LOT_KIND_LABELS, LOT_STATUS_LABELS, OFFERING_KIND_LABELS, PRICE_BASIS_LABELS, type LotDTO, type LotStatus, type OfferingKind, type ReadinessSummary,
} from "../shared/types";

export const STATUS_TONE: Record<LotStatus, Tone> = {
  draft: "neutral", ready: "info", published: "info", inquiring: "sale", reserved: "warn", sold: "good", no_sale: "neutral", withdrawn: "neutral", delivered: "good",
};
const STATUS_ICON: Record<LotStatus, string> = {
  draft: "edit", ready: "check", published: "catalog", inquiring: "contact", reserved: "clock", sold: "handshake", no_sale: "minus", withdrawn: "archive", delivered: "truck",
};

export function LotStatusChip({ status }: { status: LotStatus }) {
  return <Chip tone={STATUS_TONE[status]} icon={STATUS_ICON[status]}>{t(LOT_STATUS_LABELS[status])}</Chip>;
}

export const kindLabel = (k: OfferingKind | string | null | undefined) => (k ? t(OFFERING_KIND_LABELS[k as OfferingKind] ?? k) : "");
export const lotKindLabel = (k: string) => t(LOT_KIND_LABELS[k as keyof typeof LOT_KIND_LABELS] ?? k);

/** Translate a server message, showing ISO dates in the reader's language. */
export function tm(m: Msg | null | undefined): string {
  if (!m) return "";
  const values = Object.fromEntries(Object.entries(m.values ?? {}).map(([k, v]) => [k, typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? formatDate(v) : v]));
  return t(m.text, values);
}

export function priceText(m: Money | null | undefined, basis?: string | null): string {
  if (!m) return "";
  const amount = formatMoney(m);
  if (basis === "per_head") return t("{{amount}} a head", { amount });
  if (basis === "per_cwt") return t("{{amount}} a hundredweight", { amount });
  if (basis === "per_lb") return t("{{amount}} a pound", { amount });
  if (basis === "per_lot") return t("{{amount}} a lot", { amount });
  return amount;
}

export const basisLabel = (b: string | null | undefined) => (b ? t(PRICE_BASIS_LABELS[b as keyof typeof PRICE_BASIS_LABELS] ?? b) : "");

export function ReadinessPill({ r }: { r: ReadinessSummary }) {
  if (r.block) return <Chip tone="bad" icon="warn">{plural(r.block, "{{count}} problem blocks the sale", "{{count}} problems block the sale")}</Chip>;
  if (r.warn) return <Chip tone="warn" icon="info">{plural(r.warn, "{{count}} thing to check", "{{count}} things to check")}</Chip>;
  if (r.ok) return <Chip tone="good" icon="done">{t("Ready")}</Chip>;
  return null;
}

/** A big lot number, set like an ear tag so it reads at a glance. */
export function LotNumber({ n, size = "md" }: { n: string; size?: "md" | "lg" }) {
  return <span className={cx("sales-lot-number", size === "lg" && "sales-lot-number-lg")} aria-label={t("Lot {{lot}}", { lot: n })}>{n}</span>;
}

export function lotAnimals(lot: Pick<LotDTO, "animals" | "head_count" | "title">): string {
  if (lot.title && !lot.animals.length) return lot.title;
  if (!lot.animals.length) return lot.head_count ? plural(lot.head_count, "{{count}} head", "{{count}} head") : t("No cattle yet");
  const labels = lot.animals.map((a) => a.label);
  return labels.length > 4 ? t("{{first}} and {{count}} more", { first: labels.slice(0, 3).join(", "), count: labels.length - 3 }) : labels.join(", ");
}

const offeringSubject = (id: string): SubjectRef => ({ kind: "job", id, role: "offering" });

/** Open the record sheet for a sale record, with the sale, lots, animals and buyer filled in. */
export function recordSale(type: "sales.inquiry" | "sales.offer" | "sales.reservation" | "sales.agreement" | "sales.delivery" | "sales.transfer", opts: {
  offering?: { id: string; label?: string } | null; lots?: string[]; animals?: Array<{ id: string; label?: string }>; buyer?: { id: string; label?: string } | null; data?: Record<string, unknown>;
}) {
  const subjects: SubjectRef[] = [
    ...(opts.offering && type !== "sales.transfer" ? [{ ...offeringSubject(opts.offering.id), label: opts.offering.label }] : []),
    ...(opts.buyer ? [{ kind: "contact" as const, id: opts.buyer.id, role: "buyer", label: opts.buyer.label }] : []),
    ...(opts.animals ?? []).map((a) => ({ kind: "animal" as const, id: a.id, role: "animal", label: a.label })),
  ];
  openRecord({ initial: { type, subjects, data: { ...(opts.lots?.length && type !== "sales.transfer" ? { lots: opts.lots } : {}), ...(opts.data ?? {}) }, job_id: opts.offering?.id ?? null } });
}

/** Run a write, refresh every screen, and say what happened in plain words. */
export async function act(run: () => Promise<unknown>, done: string): Promise<boolean> {
  try {
    await run();
    await invalidateAll();
    toast(done, { tone: "good" });
    return true;
  } catch (err) {
    toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    return false;
  }
}

export const post = <T,>(path: string, body?: unknown) => api.post<T>(`/m/sales${path}`, body);
export const patch = <T,>(path: string, body: unknown) => api.patch<T>(`/m/sales${path}`, body);

export function KeyValue({ label, children, icon }: { label: string; children: ReactNode; icon?: string }) {
  return (
    <div className="sales-kv">
      <dt>{icon && <Icon name={icon} size={14} />}{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** Copy a link, falling back to showing it when the clipboard is blocked. */
export async function copyLink(path: string): Promise<void> {
  const url = new URL(path, location.origin).toString();
  try { await navigator.clipboard.writeText(url); toast(t("Link copied"), { tone: "good" }); }
  catch { toast(url, { ms: 12_000 }); }
}

/** Singular or plural with values beyond the count. Called as `many.count(...)` so the catalog check sees both keys. */
export const many = { count: (n: number, one: string, other: string, values: Record<string, unknown> = {}) => t(n === 1 ? one : other, { ...values, count: n }) };

/** The ranch's calendar date, which is what due dates are compared against. */
export function useToday(): string {
  const { data } = useMe();
  return localDate(new Date(), data?.ranch?.timezone ?? "America/Chicago");
}
